import { Injectable } from '@angular/core';
import { IAssistenza, ITotaleTipoAssistenza, StatoAssistenza, TipoAssistenza, TipoCodice } from '../models/assistenza';
import { IS1RicercaStandardParams, S1ApiCrudService, S1HttpClientService } from '@app/s1';
import { map, Observable } from 'rxjs';
import { IOperatore, IUtente } from '../models/utente';

export interface IRicercaAssistenzaParams extends IS1RicercaStandardParams, Partial<IAssistenza> { 
  tipi?: TipoAssistenza[],
  stati?: StatoAssistenza[],
  idStazione?: string,
  idOperatore?: string,
  dataDa?: Date,
  dataA?: Date,
  conBarcode?: boolean,
  tipiCodice?: TipoCodice[],
  conRimborsoAmministrativo?: boolean
}

export interface IRicercaTotaliTipoAssistenzaParams {
  idStazione: string,
  idOperatore?: string,
  dataDa: Date,
  dataA: Date
}

export interface IImpostaStatoAssistenzaParams {
  idAssistenza: string,
  stato: StatoAssistenza,
  motivazione?: string  // obbligatoria per il rifiuto
}

export interface IRicercaOperatoreParams extends IS1RicercaStandardParams { 
  idStazione?: string,
  dataDa?: Date,
  dataA?: Date
}

export interface IImpoStatoAssistenzaMassivoParams extends IRicercaAssistenzaParams { 
  statoToSet: StatoAssistenza
}

export interface IEsportaExcelAssistenzaParams extends IRicercaAssistenzaParams { }

export interface IEsportaExcelRmppParams extends IRicercaAssistenzaParams { }

export interface IEsportaMovimentiCassaParams {
  idStazione: string,
  dataDa?: Date,
  dataA?: Date
}

enum AssistenzaAPI {
  ricercaTotali = "/RicercaTotali",
  impostaStato = "/ImpostaStato",
  impostaStati = "/ImpostaStati",
  scontrino = "/Scontrino",
  report = "/Report",
  ricercaUtenti = "/RicercaUtenti",
  esportaExcel = "/EsportaExcel",
  esportaExcelRmpp = "/EsportaExcelRmpp",
  esportaRimborsi = "/EsportaRimborsi",
  esportaEntrate = "/EsportaEntrate"
}

@Injectable({
  providedIn: 'root'
})
export class AssistenzaService extends S1ApiCrudService<IAssistenza, IRicercaAssistenzaParams> {

  basePath = "/assistenza";

  constructor(s1HttpClient: S1HttpClientService) { 
    super(s1HttpClient, "/assistenza");
  }

  ricercaTotali(params: IRicercaTotaliTipoAssistenzaParams): Observable<ITotaleTipoAssistenza[]> {

    return this.s1HttpClient.post(this.path + AssistenzaAPI.ricercaTotali, params).pipe(
      map(response => {
        return  response.item?.elementi
      })
    )

  }

  impostaStato(params: IImpostaStatoAssistenzaParams): Observable<boolean> {

    const id = params.idAssistenza;
    delete params.idAssistenza;

    return this.s1HttpClient.put(`${this.path}/${id}${AssistenzaAPI.impostaStato}`, params).pipe(
      map(response => {
        return  response.outcome?.success
      })
    )

  }

  impostaStatoMassivo(params: IImpoStatoAssistenzaMassivoParams): Observable<boolean> {

    const statoToSet = params.statoToSet;
    delete params.statoToSet;

    return this.s1HttpClient.put(`${this.path}${AssistenzaAPI.impostaStati}/${statoToSet}`, params).pipe(
      map(response => {
        return  response.outcome?.success
      })
    )

  }

  scontrino(idAssistenza: string): Observable<any> {

    return this.s1HttpClient.download(`${this.path}/${idAssistenza}${AssistenzaAPI.scontrino}`).pipe(
      map(response => {
        return  response
      })
    )

  }

  report(idAssistenza: string): Observable<any> {

    return this.s1HttpClient.download(`${this.path}/${idAssistenza}${AssistenzaAPI.report}`).pipe(
      map(response => {
        return  response
      })
    )

  }

  ricercaOperatore(params: IRicercaOperatoreParams): Observable<IOperatore[]> {

    return this.s1HttpClient.post(this.path + AssistenzaAPI.ricercaUtenti, params, false).pipe(
      map(response => {
        return  response.results
      })
    )

  }

  esportaExcelAssistenza(params: IEsportaExcelAssistenzaParams): Observable<any> {

    return this.s1HttpClient.downloadPost(`${this.path}${AssistenzaAPI.esportaExcel}`, params).pipe(
      map(response => {
        return  response
      })
    )

  }

  esportaExcelRmpp(params: IEsportaExcelRmppParams): Observable<any> {

    return this.s1HttpClient.downloadPost(`${this.path}${AssistenzaAPI.esportaExcelRmpp}`, params).pipe(
      map(response => {
        return  response
      })
    )

  }

  esportaRimborsiPdf(params: IEsportaMovimentiCassaParams): Observable<any> {

    return this.s1HttpClient.downloadPost(`${this.path}${AssistenzaAPI.esportaRimborsi}`, params).pipe(
      map(response => {
        return  response
      })
    )

  }

  esportaEntratePdf(params: IEsportaMovimentiCassaParams): Observable<any> {

    return this.s1HttpClient.downloadPost(`${this.path}${AssistenzaAPI.esportaEntrate}`, params).pipe(
      map(response => {
        return  response
      })
    )

  }

}
